import {
  AppI,
  ListingType,
  DOWNTOWN,
  CityI,
  GeolocationI,
  FeedStatus,
} from "~/interfaces";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";

export type Actions = {
  // city
  removeCity: () => void;
  updateCity: (city: any | undefined) => void;

  // modal
  removeModal: () => void;
  updateModal: (modal: string) => void;

  // pathname
  removePathname: () => void;
  updatePathname: (pathname: string) => void;

  // scrollY
  updateScrollY: (scrollY: number) => void;
  updateFeedScrollY: (scrollY: number) => void;

  // calendar
  removeCalendar: () => void;
  updateCalendar: (calendar: any) => void;

  // typesense
  updateTypesense: (typesense: any) => void;

  // geolocation
  removeGeolocation: () => void;
  updateGeolocation: (geolocation: any) => void;

  // filters
  removeFilters: () => void;

  // menu
  toggleInterestMenu: () => void;
  hideMenus: () => void;
  updateMenu: (menu: any) => void;

  // reset
  resetApp: () => void;
};

export type State = {
  app: AppI;
};

const initialApp: AppI = {
  calendar: {
    dates: [new Date()],
  },
  city: undefined,
  geolocation: {
    geo_center: DOWNTOWN,
    radius: "20",
    latitude: "",
    longitude: "",
  },
  menu: {
    is_calendar_active: false,
    is_main_menu_active: false,
    is_filter_menu_active: false,
    is_like_menu_active: false,
    is_search_active: false,
    is_back_button_active: false,
    is_interest_menu_active: false,
  },
  modal: "",
  pathname: "",
  scrollY: 0,
  typesense: {
    active_start_time_unix: "",
    active_end_time_unix: "",
    query: "",
    filters: {
      type: [
        ListingType.ACTIVITY,
        ListingType.EVENT,
        ListingType.BAR,
        ListingType.RESTAURANT,
      ],
      interest_tags: [],
      price_tags: [],
      crowd_tags: [],
    },
    feed: {
      scrollY: 0,
    },
  },
};

export const useAppStore = create<State & Actions>()(
  persist(
    immer((set) => ({
      app: initialApp,

      // city
      removeCity: () =>
        set((state) => {
          state.app.city = undefined;
        }),
      updateCity: (city) =>
        set((state) => {
          state.app.city = city;
        }),

      // modal
      removeModal: () =>
        set((state) => {
          state.app.modal = "";
        }),
      updateModal: (modal) =>
        set((state) => {
          state.app.modal = modal;
        }),

      // pathname
      removePathname: () =>
        set((state) => {
          state.app.pathname = "";
        }),
      updatePathname: (pathname) =>
        set((state) => {
          state.app.pathname = pathname;
        }),

      // scrollY
      updateScrollY: (scrollY) =>
        set((state) => {
          state.app.scrollY = scrollY;
        }),
      updateFeedScrollY: (scrollY) =>
        set((state) => {
          state.app.typesense.feed.scrollY = scrollY;
        }),

      // calendar
      removeCalendar: () =>
        set((state) => {
          state.app.calendar = { ...initialApp.calendar };
        }),

      updateCalendar: (calendar) =>
        set((state) => {
          state.app.calendar = { ...state.app.calendar, ...calendar };
        }),

      // typesense
      updateTypesense: (typesense) =>
        set((state) => {
          state.app.typesense = { ...state.app.typesense, ...typesense };
        }),

      // filters
      removeFilters: () =>
        set((state) => {
          state.app.typesense.filters = {
            type: [],
            interest_tags: [],
            price_tags: [],
            crowd_tags: [],
          };
        }),
      // geolocation
      removeGeolocation: () =>
        set((state) => {
          state.app.geolocation = { ...initialApp.geolocation };
        }),
      updateGeolocation: (geolocation) =>
        set((state) => {
          state.app.geolocation = { ...state.app.geolocation, ...geolocation };
        }),

      // menu
      toggleInterestMenu: () =>
        set((state) => {
          state.app.menu.is_interest_menu_active =
            !state.app.menu.is_interest_menu_active;
        }),

      hideMenus: () =>
        set((state) => {
          state.app.menu = { ...initialApp.menu };
        }),

      updateMenu: (menu) =>
        set((state) => {
          state.app.menu = { ...state.app.menu, ...menu };
        }),

      // Reset all app info to initial state
      resetApp: () =>
        set((state) => {
          state.app = { ...initialApp };
        }),
    })),
    {
      name: "app-store",
      storage: createJSONStorage(() => sessionStorage),
      skipHydration: true,
      partialize: (state) => ({
        app: {
          calendar: state.app.calendar,
          city: state.app.city,
          geolocation: state.app.geolocation,
          pathname: state.app.pathname,
          typesense: state.app.typesense,
        },
      }),
    },
  ),
);
